/**
 * Human scores in the items table: one column per score config anyone answered
 * in an annotation queue for this experiment, after the judges' columns. A
 * result reviewed in two queues shows the latest answer. None answered: no columns.
 */

import type { Column } from "@/components/ui/data-table";
import { useHumanScores } from "@/features/annotation/hooks";
import { answerLabel } from "@/features/annotation/scores";
import type { HumanScore, ScoreConfig } from "@/features/annotation/types";
import type { ItemRow } from "../types";

function HumanCell({ config, score }: { config: ScoreConfig; score: HumanScore | undefined }) {
  if (!score) return <span className="score score-wait">·</span>;
  const fail = config.kind === "pass_fail" && score.value === "fail";
  return (
    <span className={`score ${fail ? "score-fail" : ""}`} title={`by ${score.by}`}>
      {answerLabel(config, score.value)}
    </span>
  );
}

export function useHumanScoreColumns(experimentId: string): Column<ItemRow>[] {
  const human = useHumanScores(experimentId);
  const configs = human.data?.scores ?? [];
  const byCell = new Map(
    (human.data?.values ?? []).map((v) => [`${v.item_result_id}:${v.score_config_id}`, v]),
  );
  return configs.map((c) => ({
    header: (
      <span className="inline-flex items-baseline gap-1">
        {c.name}
        <span className="text-[10px] font-normal text-faint">Human</span>
      </span>
    ),
    headClass: "num",
    className: "text-right",
    cell: (r: ItemRow) => <HumanCell config={c} score={byCell.get(`${r.id}:${c.id}`)} />,
  }));
}
